import React, { useState, useEffect, useRef } from "react";

import { EditIconStyled } from "../../styles/FileUpload";
import { Switch } from "@mui/material";
import { DeactivateTitle, NextButton, SkipButton } from "../../styles/PersonalInfoStep";
import { 
  BusinessHoursInfo,
  BusinessHoursRow,
  BusinessHoursWrapper,
  CloseButton,
  DayNameStyled,
  EditButton,
  EditorRow,
  HoursEditorContainer,
  HoursListContainer,
  HoursRow,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  OpeningHoursSubheading,
  ShopStatusDescription,
  ShopStatusInfo,
  ShopStatusRow,
  ShopStatusSection,
  ShopStatusTitle,
  StatusBadge,
  TimeDisplayStyled,
  StatusLabel,
  TimeInput,
  TimeInputContainer,
  SwitchWrapper
} from "../../styles/BusinessHoursDisplay";

interface BusinessHoursDisplayProps {
  openingHours: Array<{
    dayOfWeek: string;
    openingTime: string;
    closingTime: string;
    isClose: boolean;
  }>;
  onSave: (hours: any) => void;
}

const BusinessHoursDisplay: React.FC<BusinessHoursDisplayProps> = ({
  openingHours,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [hours, setHours] = useState<Array<{
    dayOfWeek: string;
    openingTime: string;
    closingTime: string;
    isClose: boolean;
  }>>(openingHours || []);
  
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [editingHours, setEditingHours] = useState<Array<{
    dayOfWeek: string;
    openingTime: string;
    closingTime: string;
    isClose: boolean;
  }>>(openingHours || []);

  const [hoursBeforeClosing, setHoursBeforeClosing] = useState<Array<{
    dayOfWeek: string;
    openingTime: string;
    closingTime: string;
    isClose: boolean;
  }>>(openingHours || []);

  const lastOpenHoursRef = useRef(openingHours);

  // Update local state when openingHours prop changes
  useEffect(() => {
    if (openingHours && openingHours.length > 0) {
      const hasOpenDays = openingHours.some(day => !day.isClose);
      // Persist the last known open hours
      if (hasOpenDays) {
        lastOpenHoursRef.current = openingHours;
      }
      setHours(openingHours);
      setEditingHours([...openingHours]);

      setIsShopOpen(hasOpenDays);
    }
  }, [openingHours]);
  
  useEffect(() => {
    const hasOpenDays = editingHours.some((day) => !day.isClose);
    setIsShopOpen(hasOpenDays);
  }, [editingHours]);

  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hourNum = parseInt(hours, 10);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    const hour12 = hourNum % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleDayToggle = (dayOfWeek: string, isClose: boolean) => {
    setEditingHours((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, isClose, openingTime: '', closingTime: '' }
          : day
      )
    );
  };

  const handleTimeChange = (
    dayOfWeek: string,
    type: 'openingTime' | 'closingTime',
    value: string
  ) => {
    setEditingHours((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, [type]: value } : day
      )
    );
  };

  const handleCancel = () => {
    setEditingHours([...hours]);
    const hasOpenDays = hours.some(day => !day.isClose);
    setIsShopOpen(hasOpenDays);
    setIsEditing(false);
  };

  const handleToggleShopStatus = (isOpen: boolean) => {
    setIsShopOpen(isOpen);
    if (!isOpen) {
      // When closing shop, remember the current edits and then set all days to closed
      setHoursBeforeClosing(editingHours);
      setEditingHours((prev) =>
        prev.map((day) => ({
          ...day,
          isClose: true,
          openingTime: "",
          closingTime: "",
        }))
      );
    } else {
      // When opening shop, restore the hours from before the shop was closed
      setEditingHours(hoursBeforeClosing);
    }
  };

  const save = () => {
    onSave(editingHours);
    setHours(editingHours);
    setIsEditing(false);
  };

  // Get current shop status (open/closed)
  const getCurrentStatus = () => {
    const hasOpenDays = hours.some(day => !day.isClose);
    return hasOpenDays ? "Open" : "Closed";
  };

  return (
    <BusinessHoursWrapper>
      {/* Business Hours Header Row */}
      <BusinessHoursRow className="mb-16">
        <BusinessHoursInfo>
          <DeactivateTitle>Business hours</DeactivateTitle>
          <StatusBadge $isOpen={getCurrentStatus() === "Open"}>
            {getCurrentStatus()}
          </StatusBadge>
        </BusinessHoursInfo>
        <EditButton onClick={() => setIsEditing(true)}>
          <EditIconStyled /> Edit
        </EditButton>
      </BusinessHoursRow>

      {/* Opening Hours Subheading */}
      <OpeningHoursSubheading>
        Opening Hours
      </OpeningHoursSubheading>

      {/* Hours List */}
      <HoursListContainer>
        {hours.map((day) => (
          <HoursRow $isClosed={day.isClose} key={day.dayOfWeek}>
            <DayNameStyled $isClosed={day.isClose}>{day.dayOfWeek}</DayNameStyled>
            {day.isClose ? (
              <TimeDisplayStyled $isClosed={true}>Closed</TimeDisplayStyled>
            ) : (
              <TimeDisplayStyled $isClosed={false}>
                {formatTime(day.openingTime)} - {formatTime(day.closingTime)}
              </TimeDisplayStyled>
            )}
          </HoursRow>
        ))}
      </HoursListContainer>

      {/* Edit Modal */}
      {isEditing && (
        <ModalOverlay onClick={handleCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Business Hours</ModalTitle>
              <CloseButton onClick={handleCancel}>×</CloseButton>
            </ModalHeader>

            {/* Prominent Open/Closed Toggle */}
            <ShopStatusSection>
              <ShopStatusRow>
                <ShopStatusInfo>
                  <ShopStatusTitle>
                    Shop Status
                  </ShopStatusTitle>
                  <ShopStatusDescription>
                    Shop is currently {isShopOpen ? "Open" : "Closed"}
                  </ShopStatusDescription>
                </ShopStatusInfo>
                <Switch
                  checked={isShopOpen}
                  onChange={(e) => handleToggleShopStatus(e.target.checked)}
                  size="medium"
                  sx={(theme) => ({
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: "#E39435",
                      '&:hover': {
                        backgroundColor: `rgba(227, 148, 53, 0.1)`,
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: "#E39435",
                    },
                  })}
                />
              </ShopStatusRow>
            </ShopStatusSection>

            {/* Per-day Hours Editor */}
            <HoursEditorContainer $isShopOpen={isShopOpen}>
              {editingHours.map((day) => (
                <EditorRow key={day.dayOfWeek}>
                  <DayNameStyled $isClosed={day.isClose}>{day.dayOfWeek}</DayNameStyled>
                  <TimeInputContainer>
                    <SwitchWrapper>
                      <Switch
                        checked={!day.isClose}
                        onChange={(e) => handleDayToggle(day.dayOfWeek, !e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#E39435',
                            '&:hover': {
                              backgroundColor: 'rgba(227, 148, 53, 0.1)',
                            },
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#E39435',
                          },
                        }}
                      />
                      <StatusLabel $isClosed={day.isClose}>
                        {day.isClose ? 'Closed' : 'Open'}
                      </StatusLabel>
                    </SwitchWrapper>
                    <TimeInput
                      type="time"
                      value={day.openingTime}
                      disabled={day.isClose}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'openingTime', e.target.value)}
                    />
                    <span>-</span>
                    <TimeInput
                      type="time"
                      value={day.closingTime}
                      disabled={day.isClose}
                      onChange={(e) => handleTimeChange(day.dayOfWeek, 'closingTime', e.target.value)}
                    />
                  </TimeInputContainer>
                </EditorRow>
              ))}
            </HoursEditorContainer>

            <ModalFooter>
              <SkipButton onClick={handleCancel}>Cancel</SkipButton>
              <NextButton onClick={save}>
                Confirm
              </NextButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </BusinessHoursWrapper>
  );
};



export default BusinessHoursDisplay;